// plugins/pinia-idb.js
import { toRaw, watch } from "vue"
import { openDB } from "idb"
import { useAuthStore } from "~/stores"
import { Logger } from "~/helpers/logger"

// Stores communs qui ne sont pas isolés par entreprise
// - auth, app : globaux
// - entreprise, roles : nécessaires pour l'admin
// - personnel : accumulés pour toutes les entreprises (l'admin a besoin de tous les personnels)
//               Les getters filtrent déjà par company_id quand nécessaire
const fixedStores = ["auth", "entreprise", "roles", "app", "personnel"]

// Flag pour s'assurer que le nettoyage ne s'exécute qu'une seule fois
let cleanupExecuted = false

// Stores à préserver lors du vidage du cache (auth uniquement)
const protectedStores = ["auth"]

async function initDB() {
    return openDB("lolapp", 1, {
        upgrade(db) {
            if (!db.objectStoreNames.contains("pinia")) {
                db.createObjectStore("pinia")
            }
        },
    })
}

/**
 * Nettoie les stores "admin_*" ou "0_*" qui ont été créés inutilement
 * quand l'utilisateur était en mode admin pur (sans impersonate)
 */
async function cleanupAdminStores(db) {
    try {
        const objectStoreName = "pinia"
        const allKeys = await db.getAllKeys(objectStoreName)

        // Patterns à nettoyer : stores préfixés par "0_", "admin_", "undefined_", "null_"
        const patternsToClean = [/^0_/, /^admin_/, /^undefined_/, /^null_/]

        for (const key of allKeys) {
            const shouldClean = patternsToClean.some((pattern) =>
                pattern.test(key),
            )

            if (shouldClean && !fixedStores.includes(key)) {
                Logger.info("pinia-idb", `Cleaning up unused store: ${key}`)
                await db.delete(objectStoreName, key)
            }
        }
    } catch (e) {
        Logger.error("pinia-idb", "Failed to cleanup admin stores", e)
    }
}

export const piniaIndexedDBPlugin = async ({ store }) => {
    let resolveReady
    store.ready = new Promise((resolve) => {
        resolveReady = resolve
    })

    const isCommon = fixedStores.includes(store.$id)

    const authStore = useAuthStore()
    const companyKey = authStore.companyKey

    let currentKey = isCommon ? store.$id : `${companyKey}_${store.$id}`

    const db = await initDB()
    const objectStoreName = "pinia"

    // Nettoyer les stores inutiles une seule fois au démarrage
    if (!cleanupExecuted) {
        cleanupExecuted = true
        await cleanupAdminStores(db)
    }

    async function applySavedState() {
        // Ne pas charger les stores de ressources si on est admin pur (pas impersonate)
        // car ces stores ne sont pas utilisés en mode admin
        if (!isCommon && authStore.isAdmin && !authStore.isImpersonate) {
            Logger.info(
                "pinia-idb",
                `Skipping load for ${currentKey} - admin mode without impersonate`,
            )
            return
        }

        const newSavedState = await db.get(objectStoreName, currentKey)
        if (newSavedState) {
            Logger.info("pinia-idb", `Loading state for ${currentKey}`, {
                itemCount: newSavedState.items?.length || 0,
            })

            if (store.config?.model) {
                const ModelClass = store.model
                if (Array.isArray(newSavedState.items)) {
                    newSavedState.items = newSavedState.items.map((item) =>
                        ModelClass.fromData(item),
                    )
                }
            }

            // Remplacer complètement l'état au lieu de le fusionner
            // pour éviter la contamination de données entre entreprises
            store.$state = newSavedState

            if (typeof store.hydratePeriod === "function") {
                store.hydratePeriod()
            }
        } else {
            // Aucune donnée sauvegardée, réinitialiser avec état vide
            Logger.info(
                "pinia-idb",
                `No saved state for ${currentKey}, resetting to initial state`,
            )
            if (!isCommon) {
                store.$state = { ...store.$state, items: [] }
            }
        }
    }

    await applySavedState()

    let writeTimer: ReturnType<typeof setTimeout> | null = null

    store.$subscribe((mutation, state) => {
        // Ne pas sauvegarder les stores de ressources si on est admin pur (pas impersonate)
        // pour éviter de polluer IndexedDB avec des stores vides préfixés "0_" ou "admin_"
        if (!isCommon && authStore.isAdmin && !authStore.isImpersonate) {
            return
        }

        // Debounce les écritures IndexedDB pour éviter de sérialiser
        // à chaque mutation (catastrophique lors de setItems avec modules)
        if (writeTimer) clearTimeout(writeTimer)
        writeTimer = setTimeout(async () => {
            writeTimer = null
            await db.put(
                objectStoreName,
                JSON.parse(JSON.stringify(toRaw(state))),
                currentKey,
            )
        }, 500)
    })

    if (!isCommon) {
        watch(
            () => authStore.companyKey,
            async (newCompanyKey, oldCompanyKey) => {
                // Ne rien faire si la clé n'a pas changé
                if (!newCompanyKey || newCompanyKey === oldCompanyKey) return

                Logger.info(
                    "pinia-idb",
                    `Company changed for store ${store.$id}`,
                    {
                        from: oldCompanyKey,
                        to: newCompanyKey,
                    },
                )

                // Mettre à jour la clé et charger les nouvelles données
                // Ne PAS vider manuellement les items pour éviter de sauvegarder un état vide
                // applySavedState() remplacera complètement l'état avec les bonnes données
                currentKey = `${newCompanyKey}_${store.$id}`
                await applySavedState()
            },
        )
    }

    resolveReady()
}

/**
 * Vide toutes les données IndexedDB sauf le store "auth"
 * Utilisable par les admins et managers pour nettoyer le cache local
 */
export async function clearIndexedDBCache(): Promise<{
    deletedKeys: string[]
    preservedKeys: string[]
}> {
    const db = await initDB()
    const objectStoreName = "pinia"
    const allKeys = await db.getAllKeys(objectStoreName)

    const deletedKeys: string[] = []
    const preservedKeys: string[] = []

    for (const key of allKeys) {
        if (protectedStores.includes(key as string)) {
            preservedKeys.push(key as string)
            Logger.info("pinia-idb", `Preserving protected store: ${key}`)
        } else {
            await db.delete(objectStoreName, key)
            deletedKeys.push(key as string)
            Logger.info("pinia-idb", `Deleted store: ${key}`)
        }
    }

    Logger.info(
        "pinia-idb",
        `Cache cleared: ${deletedKeys.length} stores deleted, ${preservedKeys.length} preserved`,
    )

    return { deletedKeys, preservedKeys }
}
